import React, { createRef, memo, useEffect, useRef } from 'react';

import { BarGraph } from './barGraph';
import { ScrollToPlugin } from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import gsap from 'gsap';
import { isIE } from '../helpers/isIE';

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

export const Statistic = ({
    isLarge,
    isDouble,
    showDollar,
    icon,
    value,
    unit,
    label,
    title,
    description,
    tableDataPrevYear,
    tableDataCurrentYear,
    image,
    className,
    style,
    graphData,
    isFlipped,
}) => {
    const statContainer = createRef();
    const front = createRef();
    const back = createRef();
    const backInner = createRef();
    const isToggled = createRef(false);
    const currentTimeline = createRef(null);

    useEffect(() => {
        const container = statContainer.current;
        const isDesktop = window.innerWidth > 768;
        gsap.set(container, { perspective: 800 });
        gsap.set(back.current, { rotationY: -180 });
        gsap.set(front.current, { transformStyle: 'preserve-3d' });
        gsap.set([back.current, front.current], { backfaceVisibility: 'hidden' });
        gsap.set(backInner.current, { opacity: 0 });

        back.current.setAttribute('tabindex', -1);

        //Intro animation
        if (!isIE) {
            gsap.set(container.parentNode, { perspective: 1500 });
            gsap.from(container, {
                scrollTrigger: {
                    trigger: container,
                    start: 'top bottom-=70px',
                    end: '+=50',
                    scrub: 2,
                    once: true,
                },
                duration: 1.5,
                opacity: 0,
                rotationX: 90,
                ease: 'back.out(1.5)',
            });
        }

        const handleMouseEnter = () => {
            // if (!front.current) return; //Avoid hot reload errors
            gsap.to(front.current, { boxShadow: '0 12px 50px 0 rgba(0, 0, 0, 0.2)', translateY: -4, duration: 0.2 });
        };

        const handleMouseLeave = () => {
            // if (!front.current) return; //Avoid hot reload errors
            gsap.to(front.current, { boxShadow: 'none', translateY: 0, duration: 0.2 });
        };

        const handleClickOutside = (e) => {
            if (!container.contains(e.target)) {
                if (isToggled.current) {
                    const stats = document.getElementsByClassName('stat');

                    const expandingAnotherStat = [...stats].some((el) => el.contains(e.target));
                    collapse(expandingAnotherStat);
                }
            }
        };

        const handleKeyPress = (e) => {
            if (e.keyCode === 27 && isToggled.current) {
                collapse();
            }
        };

        if (container) {
            if (isDesktop) {
                container.addEventListener('mouseenter', handleMouseEnter);
                container.addEventListener('mouseleave', handleMouseLeave);
            }
            container.addEventListener('keydown', handleKeyPress);
            //Detect click outside
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            container.removeEventListener('mouseenter', handleMouseEnter);
            container.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('keydown', handleKeyPress);
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const expand = () => {
        if (typeof window === 'undefined') return;
        const isDesktop = window.innerWidth > 768;

        //Keyboard navigation
        back.current.removeAttribute('tabindex');
        front.current.setAttribute('tabindex', -1);
        back.current.focus();

        const newIndex = window.lastIndex ? window.lastIndex + 2 : 5;
        window.lastIndex = newIndex;
        var tl = gsap.timeline();
        currentTimeline.current && currentTimeline.current.kill();
        const viewportOffset = statContainer.current.getBoundingClientRect();

        //Calculate required sizes. Have to temporarily change the width to get this calculation right.
        !isLarge && !isDouble && gsap.set(back.current, { width: '200%' });
        const heightIncrease = backInner.current.offsetHeight;
        !isLarge && !isDouble && gsap.set(back.current, { width: '100%' });
        const width = document.querySelector('.stat--large').offsetWidth;

        if (back.current.offsetHeight < heightIncrease) {
            tl.to(back.current, { height: heightIncrease + 'px', ease: 'back.out(1.2)', duration: 0.3 }, 0.3);
        }

        gsap.set(statContainer.current.parentNode, { zIndex: newIndex });
        gsap.set('.modal-fade', { display: 'block', zIndex: newIndex - 1 });

        tl.to(front.current, { rotationY: 180, ease: 'back.out(1.2)', duration: 0.8 }, 0);
        tl.to(back.current, { rotationY: 0, ease: 'back.out(1.2)', duration: 0.8 }, 0);
        tl.to(backInner.current, { opacity: 1, ease: 'back.out(1.2)', duration: 0.4 }, !isLarge && !isDouble ? 0.4 : 0.2);

        tl.to('.modal-fade', { opacity: 1, duration: 0.3 }, 0.3);

        if (!isLarge && !isDouble) {
            tl.to(back.current, { width: width + 'px', ease: 'back.out(1.2)', duration: 0.3 }, 0.3); //Small

            //Nudge it left if it's on the right side of the window. The 20 just gives a little safe offset for items starting right in the middle
            if (viewportOffset.left > window.innerWidth / 2 - 20) {
                tl.to(back.current, { translateX: '-50%', ease: 'back.out(1.2)', duration: 0.3 }, 0.3); //Small
            }
        }

        //Scroll the view to fit the statistic
        const bottomPosition = viewportOffset.top + heightIncrease;
        const offsetTop = statContainer.current.getBoundingClientRect().top + window.scrollY;
        console.log('window.scrollY', window.scrollY);
        console.log('offsetTop', offsetTop);

        const scrollPosition = !isDesktop ? offsetTop - 62 : offsetTop - statContainer.current.offsetHeight / (isLarge ? 2 : 1);

        if (bottomPosition > window.innerHeight) {
            gsap.to(window, { duration: 0.4, scrollTo: { y: scrollPosition } });
        }

        //Nudge it up if sitting outside the document (near footer on small devices)

        if (offsetTop + heightIncrease > document.documentElement.offsetHeight) {
            const verticalNudge = document.documentElement.offsetHeight - (offsetTop + heightIncrease);
            tl.to(back.current, { translateY: verticalNudge, ease: 'back.out(1.2)', duration: 0.3 }, 0.3); //Small
        }

        currentTimeline.current = tl;
    };

    const collapse = (expandingAnotherStat = false) => {
        var tl = gsap.timeline();
        currentTimeline.current && currentTimeline.current.kill();
        const delay = isLarge ? 0 : 0.1;

        //Deal with keyboard nav
        back.current.setAttribute('tabindex', -1);
        front.current.removeAttribute('tabindex');
        if (!expandingAnotherStat) {
            front.current.focus();
        }

        if (!expandingAnotherStat) {
            tl.to('.modal-fade', { opacity: 0, duration: 0.3 }, 0);
            tl.to('.modal-fade', { display: 'none' }, 0.3);
        }

        tl.to(back.current, { height: '100%', width: '100%', translateX: 0, translateY: 0, ease: 'back.out(1.2)', duration: 0.3 }, 0); //Small
        tl.to(front.current, { rotationY: 0, ease: 'back.out(1.2)', duration: 0.8 }, delay);
        tl.to(back.current, { rotationY: -180, ease: 'back.out(1.2)', duration: 0.8 }, delay);
        tl.to(backInner.current, { opacity: 0, ease: 'back.out(1.2)', duration: 0.4 }, 0);

        currentTimeline.current = tl;
    };

    const handleClick = () => {
        if (!isToggled.current) {
            expand();
        } else {
            collapse();
        }
        isToggled.current = !isToggled.current;
    };

    let statClass = 'stat';

    if (isLarge) statClass = statClass + ' stat--large';
    if (isLarge && !image) statClass = statClass + ' stat--large-no-image';
    if (className) statClass = statClass + ' ' + className; //Custom classname for varying styles
    if (isDouble) statClass = statClass + ' stat--double';
    if (isDouble && image) statClass = statClass + ' stat--double-image';
    if (isFlipped) statClass = statClass + ' stat--flipped';
    if (!icon) statClass = statClass + ' stat--no-icon';
    return (
        <div className={statClass} style={(isDouble && { width: '100%' }, style && style)}>
            {/* {imagePath && <div className="stat__image " style={{ backgroundImage: `url(${imagePath})` }}></div>} */}
            <div className="stat__wrap" ref={statContainer}>
                <button className="stat__front" onClick={handleClick} ref={front}>
                    <div className="stat__content">
                        <div>
                            {icon && <div className="stat__icon">{icon}</div>}
                            <div className="stat__details" data-equ-numericcounter='{"value": 195, "duration": 1000, "divider": ""}'>
                                <strong className="u-block-element">
                                    <span>
                                        {showDollar && '$'}
                                        <span className="numeric-counter__value">{value && value} </span>
                                    </span>
                                    {unit && unit}
                                </strong>
                                {label && <p>{label}</p>}
                            </div>
                        </div>
                    </div>
                    {image && <div className="stat__image ">{image}</div>}
                    <span className="u-sr-only">Button to open more info about {label}</span>
                </button>
                <button className="stat__back" onClick={handleClick} ref={back}>
                    <div ref={backInner}>
                        <div className="btn-close btn-close--big"></div>
                        <div className="stat__back-header">
                            {icon && <div className="stat__back-icon">{icon}</div>}
                            {/* On smaller cards, title only and 1 col */}
                            <h2 className="stat__back-title">{title && title}</h2>
                        </div>
                        <div className="stat__back-main">
                            {description && typeof description === 'string' && <p>{description}</p>}
                            {description && typeof description !== 'string' && description}
                            {graphData && <BarGraph data={graphData} label={label} />}
                            {(tableDataPrevYear || tableDataCurrentYear) && (
                                <div className="table-responsive">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Period</th>
                                                <th>Actual</th>
                                                <th>Target</th>
                                                <th>Variance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableDataPrevYear && (
                                                <tr>
                                                    {tableDataPrevYear.map((value, index) => (
                                                        <td key={`${label.replace(' ', '-').toLowerCase() + 'tableCell' + index}`}>
                                                            {value}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )}
                                            {tableDataCurrentYear && (
                                                <tr>
                                                    {tableDataCurrentYear.map((value, index) => (
                                                        <td key={`${label.replace(' ', '-').toLowerCase() + 'tableCell' + index}`}>
                                                            {value}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                    <span className="u-sr-only">Button to close more info about {label}</span>
                </button>
            </div>
        </div>
    );
};
